import clsx from 'clsx'
import { match } from 'ts-pattern'
import useChargeLimitSlider from '@/authenticated/components/ChargeForecastDashboardView/ChargeForecastSlider/useViewModel'

export type BatteryTone = 'normal' | 'low' | 'critical' | 'unknown'

export const BATTERY_TONE_BACKGROUNDS: Record<BatteryTone, string> = {
  normal: 'bg-themed-green-500',
  low: 'bg-themed-orange-500',
  // themed red-500 is the darker #dd0303 Figma uses for critical
  critical: 'bg-themed-red-500',
  // No battery reading to show
  unknown: 'bg-themed-grey-400',
}

// The charged fill turns orange at 20% and red at 10%
export function getBatteryTone(batteryPercent: number | null): BatteryTone {
  if (batteryPercent === null) {
    return 'unknown'
  }

  return match(batteryPercent)
    .when(
      (percent) => percent <= 10,
      () => 'critical' as const,
    )
    .when(
      (percent) => percent <= 20,
      () => 'low' as const,
    )
    .otherwise(() => 'normal' as const)
}

function clampPercent(value: number) {
  return Math.min(Math.max(value, 0), 100)
}

type ViewProps = {
  // null when the car hasn't reported a battery level
  batteryPercent: number | null
  chargeLimit: number
  step?: number
  onChange?: (chargeLimit: number) => void
  onStartDragging?: () => void
  onStoppedDragging?: () => void
}

/**
 * Presentational half, exported for Storybook: the battery level with a
 * draggable charge limit thumb.
 */
export function BatteryLimitBarView({
  batteryPercent: rawBatteryPercent,
  chargeLimit: rawChargeLimit,
  step = 1,
  onChange,
  onStartDragging,
  onStoppedDragging,
}: ViewProps) {
  const batteryPercent =
    rawBatteryPercent === null
      ? null
      : clampPercent(Math.round(rawBatteryPercent))
  const chargeLimit = clampPercent(Math.round(rawChargeLimit))

  return (
    <div className="flex flex-col w-full gap-6">
      <div className="relative w-full h-7">
        <div className="absolute inset-x-0 h-3 rounded-md top-2 bg-themed-grey-200" />
        <div
          className={clsx(
            'absolute left-0 h-3 rounded-md top-2 transition-[width] duration-500',
            BATTERY_TONE_BACKGROUNDS[getBatteryTone(batteryPercent)],
          )}
          style={{ width: `${batteryPercent ?? 0}%` }}
        />
        <div
          aria-hidden
          className="absolute top-0 box-border bg-white border-2 border-solid rounded-full pointer-events-none w-7 h-7 border-themed-grey-900"
          style={{
            left: `clamp(0px, calc(${chargeLimit}% - 14px), calc(100% - 28px))`,
          }}
        />
        <input
          id="charge-forecast-v2-charge-limit"
          type="range"
          min={0}
          max={100}
          step={step}
          value={chargeLimit}
          aria-label="Charge limit"
          aria-valuetext={`Charge limit ${chargeLimit}%`}
          onChange={(event) => onChange?.(Number(event.target.value))}
          onPointerDown={onStartDragging}
          onPointerUp={onStoppedDragging}
          onKeyUp={onStoppedDragging}
          className="absolute inset-0 w-full h-full m-0 opacity-0 cursor-pointer"
        />
      </div>
      <div className="flex justify-between w-full text-xs font-medium leading-5 text-themed-grey-500">
        <span>
          {batteryPercent === null
            ? 'Battery level unknown'
            : `${batteryPercent}%`}
        </span>
        <span>Charge limit to {chargeLimit}%</span>
      </div>
    </div>
  )
}

type Props = {
  vehicleId: number
  // Passed in so an unreported battery level can show as unknown instead of 0%
  batteryPercent?: number | null
  onChargeLimitChange?: (chargeLimit: number) => void
}

/**
 * Dragging reuses the existing charge forecast slider view model, which saves
 * the new limit to the vehicle's charge schedule.
 */
export default function BatteryLimitBar({
  vehicleId,
  batteryPercent,
  onChargeLimitChange,
}: Props) {
  const slider = useChargeLimitSlider({
    vehicleId,
    loading: false,
    onChange: onChargeLimitChange,
  })

  return (
    <BatteryLimitBarView
      batteryPercent={
        batteryPercent === undefined ? slider.currentCharge : batteryPercent
      }
      chargeLimit={slider.chargeLimit}
      step={slider.step}
      onChange={slider.onChange}
      onStartDragging={slider.onStartDragging}
      onStoppedDragging={slider.onStoppedDragging}
    />
  )
}
